import { useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { DefectStatus } from '@bhops/shared';
import { canEditMfg } from '@/permissions';
import { partsApi } from '@/api/parts';
import { extractApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/components/Toast';
import { Button } from '@/components/Form';
import { ConfirmDialog } from '@/components/ConfirmDialog';
// 추가수정 2차(26-07-27) §2 — 표시 날짜는 KST 정본으로.
import { kstYmd } from '@/lib/format-date';

// docs/plan/v0.4.0-parts-solution-defect-lot/plan.md §6.3 — LOT 영향권 페이지.
//
// 핵심 액션:
//   - CONFIRMED 판정 → 운영자 검토 후 Recall 트리거
//   - CLEARED 처리 (false positive)
// 데이터: GET /parts/lots/:id/impact (활성 로봇 + 매장/고객 집계 + 관련 SO).

const DEFECT_LABEL: Record<DefectStatus, string> = {
  NORMAL: '정상',
  SUSPECTED: '의심',
  CONFIRMED: '확정',
  CLEARED: '해제',
};

export function PartLotImpactPage() {
  const { id = '' } = useParams<{ id: string }>();
  const role = useAuthStore((s) => s.user?.role);
  // 계획서(2026-08-20) 요구1 ② — 서버가 리콜(@Post lots/:id/recall)도 결함 판정
  // (@Patch defect-status)도 같은 @Roles(ADMIN·MANAGER·MFG_MANAGER)로 통일했다.
  // 종전엔 canRecall(ADMIN·MANAGER) / canTriage(+MFG) 두 게이트였는데, 서버 규칙이
  // 하나가 된 순간 두 변수는 같은 뜻의 복사 두 벌이라 하나로 합친다 — 제조팀은
  // 리콜 버튼이 숨어 '서버는 되는데 버튼이 없음' 상태였다.
  const canJudgeLot = role != null && canEditMfg(role);
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [recallOpen, setRecallOpen] = useState(false);
  const [recallReason, setRecallReason] = useState('');
  const [clearOpen, setClearOpen] = useState(false);
  // NORMAL → SUSPECTED 전환 (Recall 전단계)
  const [markSuspectedOpen, setMarkSuspectedOpen] = useState(false);
  const [suspectedReason, setSuspectedReason] = useState('');

  const q = useQuery({
    queryKey: ['part-lot-impact', id],
    queryFn: () => partsApi.getLotImpact(id),
    enabled: !!id,
    staleTime: 15_000,
  });

  const recallMx = useMutation({
    mutationFn: () => partsApi.recall(id, { reason: recallReason.trim() || undefined }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['part-lot-impact', id] });
      qc.invalidateQueries({ queryKey: ['part-lots'] });
      qc.invalidateQueries({ queryKey: ['fleet', 'defect-lots'] });
      toast.success(
        `리콜을 마쳤습니다. 서비스 오더 ${result.createdServiceOrders.length}건을 자동으로 만들었습니다.`,
      );
      setRecallOpen(false);
      setRecallReason('');
    },
    onError: (err) => toast.error(extractApiError(err), '리콜 실패'),
  });

  const clearMx = useMutation({
    mutationFn: () =>
      partsApi.updateLotDefectStatus(id, { defectStatus: 'CLEARED', reason: 'False positive' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['part-lot-impact', id] });
      qc.invalidateQueries({ queryKey: ['part-lots'] });
      qc.invalidateQueries({ queryKey: ['fleet', 'defect-lots'] });
      toast.success('LOT를 정상으로 처리했습니다.');
      setClearOpen(false);
    },
    onError: (err) => toast.error(extractApiError(err), '해제 실패'),
  });

  // NORMAL → SUSPECTED 전환. Recall 전 반드시 거쳐야 하는 게이트.
  const markSuspectedMx = useMutation({
    mutationFn: () =>
      partsApi.updateLotDefectStatus(id, {
        defectStatus: 'SUSPECTED',
        reason: suspectedReason.trim() || '운영자 수동 이상 마킹',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['part-lot-impact', id] });
      qc.invalidateQueries({ queryKey: ['part-lots'] });
      qc.invalidateQueries({ queryKey: ['fleet', 'defect-lots'] });
      toast.success('LOT를 이상 의심으로 표시했습니다. 필요하면 리콜할 수 있습니다.');
      setMarkSuspectedOpen(false);
      setSuspectedReason('');
    },
    onError: (err) => toast.error(extractApiError(err), '이상 의심 표시 실패'),
  });

  if (q.isLoading) {
    return <div style={{ padding: 32, color: 'var(--fg-muted)' }}>불러오는 중…</div>;
  }
  if (q.error || !q.data) {
    return (
      <div style={{ padding: 32, color: 'var(--fg-danger)' }}>
        {q.error ? extractApiError(q.error) : 'LOT를 찾을 수 없습니다.'}
      </div>
    );
  }
  const { lot, affectedRobots, storeBreakdown, customerBreakdown, serviceOrders } = q.data;
  // foot-gun 방지: NORMAL 상태 LOT은 직접 Recall 불가 — 먼저 SUSPECTED로 이상 마킹해야 함.
  // CLEARED 도 "이상 마킹" 으로 다시 SUSPECTED 전환 허용 (운영자가 신규 증거 발견 시 cron
  // 자동 감지를 기다리지 않고 즉시 재의심 가능하도록).
  const showRecall = canJudgeLot && lot.defectStatus === 'SUSPECTED';
  const showMarkSuspected =
    canJudgeLot && (lot.defectStatus === 'NORMAL' || lot.defectStatus === 'CLEARED');
  const showClear = canJudgeLot && lot.defectStatus === 'SUSPECTED';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div>
        {/* windows-ux-edit (2026-06, 슬라이드5)로 부품 LOT 리스트 라우트가 빠졌다가
            오너 확정 2026-08-20 결정 4 로 '부품 입고' 라우트가 복원됐다. 진입 경로가
            둘(부품 입고 목록 · 로봇 상세 '부품' 탭)이라 고정 링크 대신 브라우저
            뒤로가기로 복귀한다 — 온 곳으로 돌아가는 것이 맞다.

            오너 결정 2026-08-28 로 '부품 입고' 가 **메뉴에서 숨겨졌다**(라우트는 살아
            있다 — nav-hidden.ts). 그래도 뒤로가기가 계속 맞다: 이제 이 화면에 오는
            주된 길이 로봇 상세 '부품' 탭이므로, 고정 링크를 '/parts/lots' 로 박으면
            숨긴 메뉴로 가는 **입구가 되살아나고** 사용자를 온 곳이 아닌 데로 보낸다.
            ⚠ '진입 경로가 하나뿐이니 고정 링크로 바꾸자' 는 유혹을 여기서 막는다. */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="kuun-caption"
          style={{
            ...crumbStyle,
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            font: 'inherit',
          }}
        >
          ← 뒤로
        </button>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginTop: 6 }}>
          <div style={{ flex: 1 }}>
            <h1 className="kuun-h2" style={{ margin: 0 }}>
              {lot.lotCode}
            </h1>
            <div className="kuun-caption" style={{ marginTop: 4, color: 'var(--fg-muted)' }}>
              {lot.partCatalog.displayName} · SKU {lot.partCatalog.sku}
              {lot.supplierName ? ` · 공급사 ${lot.supplierName}` : ''}
              {' · '} 입고 {kstYmd(lot.receivedAt) ?? '-'}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <DefectBadge status={lot.defectStatus} />
              {showMarkSuspected && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setMarkSuspectedOpen(true)}
                  disabled={markSuspectedMx.isPending}
                >
                  이상 의심으로 표시
                </Button>
              )}
              {showClear && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setClearOpen(true)}
                  disabled={clearMx.isPending}
                >
                  정상 처리
                </Button>
              )}
              {showRecall && (
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => setRecallOpen(true)}
                  disabled={recallMx.isPending}
                >
                  리콜 실행
                </Button>
              )}
            </div>
            {/* 상태별 안내 — Recall 가능 조건 명시 */}
            {lot.defectStatus === 'CONFIRMED' && (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--fg-muted)',
                  textAlign: 'right',
                  maxWidth: 320,
                }}
              >
                이미 결함이 확정된 LOT입니다. 더 할 조치는 없습니다.
              </div>
            )}
            {lot.defectStatus === 'NORMAL' && (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--fg-muted)',
                  textAlign: 'right',
                  maxWidth: 320,
                }}
              >
                정상 상태입니다. 결함이 의심되면 먼저 "이상 의심으로 표시"를 누른 뒤 리콜할 수
                있습니다.
              </div>
            )}
            {lot.defectStatus === 'CLEARED' && (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--fg-muted)',
                  textAlign: 'right',
                  maxWidth: 320,
                }}
              >
                정상으로 처리된 LOT입니다. 새 증거가 나오면 &quot;이상 의심으로 표시&quot;로 다시
                바꿀 수 있고, 자동 감지가 같은 문제를 찾으면 의심 상태로 돌아갈 수도 있습니다.
              </div>
            )}
            {/* Recall 사유 입력 — 확인 다이얼로그 전에 미리 입력 */}
            {showRecall && (
              <div style={{ width: 320 }}>
                <div className="kuun-label" style={{ marginBottom: 4, fontSize: 11 }}>
                  리콜 사유 (선택)
                </div>
                <textarea
                  value={recallReason}
                  onChange={(e) => setRecallReason(e.target.value)}
                  placeholder="예: 제조사에서 LOT 결함을 알려 옴"
                  style={{
                    width: '100%',
                    minHeight: 60,
                    padding: 8,
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--r-input)',
                    fontSize: 12,
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            )}
            {/* 이상 마킹 사유 입력 — NORMAL → SUSPECTED 전환 시 운영자 컨텍스트 기록 */}
            {showMarkSuspected && (
              <div style={{ width: 320 }}>
                <div className="kuun-label" style={{ marginBottom: 4, fontSize: 11 }}>
                  이상 의심 사유 (선택)
                </div>
                <textarea
                  value={suspectedReason}
                  onChange={(e) => setSuspectedReason(e.target.value)}
                  placeholder="예: 사용 중 이상 진동 보고가 여러 건 있음"
                  style={{
                    width: '100%',
                    minHeight: 60,
                    padding: 8,
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--r-input)',
                    fontSize: 12,
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
        }}
      >
        <StatCard label="입고 / 잔여" value={`${lot.receivedQty} / ${lot.remainingQty}`} />
        <StatCard label="영향 받는 활성 로봇" value={String(affectedRobots.length)} highlight />
        <StatCard label="영향 매장" value={String(storeBreakdown.length)} />
        <StatCard label="영향 고객사" value={String(customerBreakdown.length)} />
      </div>

      {/* Affected robots */}
      <Section title={`영향 받는 활성 로봇 (${affectedRobots.length})`}>
        {affectedRobots.length === 0 ? (
          <div style={empty}>장착된 활성 부품이 없습니다. 이미 모두 교체되었습니다.</div>
        ) : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>로봇</th>
                <th style={th}>모델</th>
                <th style={th}>슬롯</th>
                <th style={th}>매장</th>
                <th style={th}>고객사</th>
                <th style={th}>장착일</th>
              </tr>
            </thead>
            <tbody>
              {affectedRobots.map((r) => (
                <tr
                  key={r.robotId}
                  style={{ borderTop: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                  onClick={() => navigate(`/robots/${r.robotId}`)}
                >
                  <td style={tdMono}>
                    <Link
                      to={`/robots/${r.robotId}`}
                      style={{ color: 'var(--blue-500)' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {r.serialShort
                        ? `#${r.serialShort}`
                        : (r.serialLong ?? r.robotId.slice(0, 8))}
                    </Link>
                  </td>
                  <td style={td}>{r.modelDisplayName}</td>
                  <td style={td}>{r.slot}</td>
                  <td style={td}>{r.storeName ?? '–'}</td>
                  <td style={td}>{r.customerName ?? '–'}</td>
                  <td style={{ ...tdMono, fontSize: 11 }}>{kstYmd(r.installedAt) ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* Store + Customer breakdowns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Section title={`매장별 집계 (${storeBreakdown.length})`}>
          {storeBreakdown.length === 0 ? (
            <div style={empty}>–</div>
          ) : (
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>매장</th>
                  <th style={{ ...th, textAlign: 'right' }}>로봇</th>
                </tr>
              </thead>
              <tbody>
                {storeBreakdown.map((s) => (
                  <tr key={s.storeId} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={td}>{s.storeName}</td>
                    <td style={{ ...tdMono, textAlign: 'right', fontWeight: 600 }}>
                      {s.robotCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
        <Section title={`고객사별 집계 (${customerBreakdown.length})`}>
          {customerBreakdown.length === 0 ? (
            <div style={empty}>–</div>
          ) : (
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>고객사</th>
                  <th style={{ ...th, textAlign: 'right' }}>로봇</th>
                </tr>
              </thead>
              <tbody>
                {customerBreakdown.map((c) => (
                  <tr key={c.customerId} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={td}>{c.customerName}</td>
                    <td style={{ ...tdMono, textAlign: 'right', fontWeight: 600 }}>
                      {c.robotCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>

      {/* Related Service Orders */}
      <Section title={`관련 서비스 오더 (${serviceOrders.length})`}>
        {serviceOrders.length === 0 ? (
          <div style={empty}>
            관련 서비스 오더가 없습니다. 자동 감지나 직접 진단이 있으면 여기에 나타납니다.
          </div>
        ) : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>SO</th>
                <th style={th}>등급</th>
                <th style={th}>상태</th>
                <th style={th}>발생일</th>
              </tr>
            </thead>
            <tbody>
              {serviceOrders.map((so) => (
                <tr key={so.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={tdMono}>
                    <Link
                      to={`/service-orders`}
                      state={{ openId: so.id }}
                      style={{ color: 'var(--blue-500)' }}
                    >
                      {so.humanId}
                    </Link>
                  </td>
                  <td style={td}>{so.severity}</td>
                  <td style={td}>{so.status}</td>
                  <td style={{ ...tdMono, fontSize: 11 }}>{kstYmd(so.createdAt) ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* Recall confirm dialog */}
      <ConfirmDialog
        open={recallOpen}
        title="LOT 리콜"
        message={
          `"${lot.lotCode}"의 결함을 확정하고, 영향받는 로봇 ${affectedRobots.length}대마다 ` +
          `P2 등급 부품 교체 서비스 오더를 자동으로 만듭니다.\n\n` +
          `되돌릴 수 없습니다. 사유는 서비스 오더 설명에 기록되고 ` +
          `Slack #defect-lot 채널로 알림이 갑니다.`
        }
        confirmLabel={`${affectedRobots.length}대 리콜`}
        danger
        busy={recallMx.isPending}
        onConfirm={() => recallMx.mutate()}
        onCancel={() => {
          if (recallMx.isPending) return;
          setRecallOpen(false);
        }}
      />

      <ConfirmDialog
        open={clearOpen}
        title="LOT 정상 처리"
        message={
          `"${lot.lotCode}"의 결함 의심을 풀고 정상으로 처리하시겠습니까?\n\n` +
          `잘못 의심했을 때 씁니다. 나중에 자동 감지가 같은 문제를 다시 찾으면 의심 상태로 돌아옵니다.`
        }
        confirmLabel="정상 처리"
        busy={clearMx.isPending}
        onConfirm={() => clearMx.mutate()}
        onCancel={() => {
          if (clearMx.isPending) return;
          setClearOpen(false);
        }}
      />

      {/* 이상 마킹 (NORMAL → SUSPECTED) 확인 다이얼로그 */}
      <ConfirmDialog
        open={markSuspectedOpen}
        title="LOT 이상 의심 표시"
        message={
          `"${lot.lotCode}"의 상태를 결함 의심으로 바꿉니다.\n\n` +
          `이 단계에서는 서비스 오더가 만들어지지 않고, 이후에 리콜을 진행할 수 있습니다. ` +
          `더 자주 지켜보게 되며 Slack #defect-lot 채널로 알림이 갑니다.`
        }
        confirmLabel="의심으로 표시"
        busy={markSuspectedMx.isPending}
        onConfirm={() => markSuspectedMx.mutate()}
        onCancel={() => {
          if (markSuspectedMx.isPending) return;
          setMarkSuspectedOpen(false);
          setSuspectedReason('');
        }}
      />
    </div>
  );
}

function DefectBadge({ status }: { status: DefectStatus }) {
  const color =
    status === 'CONFIRMED'
      ? { fg: 'var(--fg-danger)', bg: 'var(--bg-danger-subtle)', border: 'var(--fg-danger)' }
      : status === 'SUSPECTED'
        ? {
            fg: 'var(--fg-on-warning-tint)',
            bg: 'var(--bg-warning-subtle)',
            border: 'var(--severity-warning)',
          }
        : status === 'CLEARED'
          ? {
              fg: 'var(--fg-on-success-tint)',
              bg: 'transparent',
              border: 'var(--green-700)',
            }
          : { fg: 'var(--fg-muted)', bg: 'transparent', border: 'var(--border-default)' };
  return (
    <span
      style={{
        padding: '4px 12px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        color: color.fg,
        background: color.bg,
        border: `1px solid ${color.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {DEFECT_LABEL[status]}
    </span>
  );
}

function StatCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      style={{
        border: `1px solid ${highlight ? 'var(--severity-urgent)' : 'var(--border-default)'}`,
        background: highlight ? 'var(--bg-danger-subtle)' : 'var(--bg-surface)',
        borderRadius: 'var(--r-card)',
        padding: 14,
      }}
    >
      <div className="kuun-label" style={{ color: 'var(--fg-muted)' }}>
        {label}
      </div>
      <div
        className="kuun-kpi"
        style={{
          marginTop: 4,
          color: highlight ? 'var(--fg-danger)' : 'var(--fg-default)',
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--r-card)',
        background: 'var(--bg-surface)',
        padding: 12,
      }}
    >
      <h3 className="kuun-h4" style={{ margin: '0 0 8px' }}>
        {title}
      </h3>
      <div style={{ overflow: 'auto' }}>{children}</div>
    </section>
  );
}

const crumbStyle: CSSProperties = {
  color: 'var(--fg-muted)',
  textDecoration: 'none',
};
const table: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};
const th: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
};
const td: CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' };
const tdMono: CSSProperties = { ...td, fontFamily: 'var(--font-mono)' };
const empty: CSSProperties = {
  padding: 16,
  textAlign: 'center',
  color: 'var(--fg-muted)',
  fontSize: 12,
};
